import React, { useCallback, useRef, useState } from 'react';
import { Code, FileCode } from 'lucide-react';
import { Panel } from '../panels/Panel';
import { usePanelStore } from '../../store/panelStore';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { ScriptEditorToolbar } from './ScriptEditorToolbar';
import { ScriptEditorTabs } from './ScriptEditorTabs';
import { ScriptEditorSidebar } from './ScriptEditorSidebar';
import { ScriptEditorHeader } from './ScriptEditorHeader';
import { BottomTabs } from './BottomTabs';
import { MonacoWrapper } from './MonacoWrapper';
import { cn } from '../../utils/cn';

export const ScriptEditorPanel: React.FC = () => {
  const panel = usePanelStore((state) => state.panels['script-editor']);

  const {
    activeTabId,
    sidebarVisible,
    sidebarWidth,
    bottomPanelVisible,
    bottomPanelHeight,
    setSidebarWidth,
  } = useScriptEditorStore();

  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const sidebarResizeRef = useRef<HTMLDivElement>(null);

  // Handle sidebar resize
  const handleSidebarResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingSidebar(true);

    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      setSidebarWidth(startWidth + deltaX);
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth, setSidebarWidth]);

  if (!panel.visible) {
    return null;
  }

  return (
    <Panel panel={panel} className="flex flex-col h-full" data-script-editor>
      {/* Toolbar */}
      <ScriptEditorToolbar />

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Sidebar */}
        {sidebarVisible && (
          <>
            <div
              style={{ width: sidebarWidth }}
              className="flex-shrink-0 overflow-hidden"
            >
              <ScriptEditorSidebar />
            </div>

            {/* Sidebar Resize Handle */}
            <div
              ref={sidebarResizeRef}
              onMouseDown={handleSidebarResizeStart}
              className={cn(
                'w-1 flex-shrink-0 cursor-col-resize transition-colors',
                isResizingSidebar ? 'bg-blue-500' : 'bg-zinc-800 hover:bg-blue-500'
              )}
            />
          </>
        )}

        {/* Editor Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Tabs */}
          <ScriptEditorTabs />

          {/* Editor/Empty State Container */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Monaco Editor or Empty State */}
            <div
              className="flex-1 min-h-0 overflow-hidden"
              style={{
                height: bottomPanelVisible
                  ? `calc(100% - ${bottomPanelHeight}px)`
                  : '100%',
              }}
            >
              {activeTabId ? (
                <MonacoWrapper fileId={activeTabId} className="h-full" />
              ) : (
                <EmptyState />
              )}
            </div>

            {/* Bottom Panel */}
            {bottomPanelVisible && (
              <div
                style={{ height: bottomPanelHeight }}
                className="flex-shrink-0 overflow-hidden"
              >
                <BottomTabs />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <ScriptEditorHeader />
    </Panel>
  );
};

// Empty state component
const EmptyState: React.FC = () => {
  const { createFile } = useScriptEditorStore();

  const handleCreateNewFile = useCallback(async () => {
    await createFile('assets/scripts/NewScript.ts', 'typescript');
  }, [createFile]);

  return (
    <div className="flex flex-col items-center justify-center h-full bg-zinc-900 text-zinc-400">
      <div className="flex flex-col items-center max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-6">
          <Code className="w-8 h-8 text-zinc-600" />
        </div>

        <h2 className="text-xl font-medium text-zinc-300 mb-2">
          Script Editor
        </h2>

        <p className="text-sm text-zinc-500 mb-6">
          Create and edit TypeScript and JavaScript scripts for your game.
          Use the explorer on the left to create or open files.
        </p>

        <button
          onClick={handleCreateNewFile}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
        >
          <FileCode className="w-4 h-4" />
          Create New Script
        </button>

        <div className="mt-8 text-xs text-zinc-600">
          <p className="mb-2">Keyboard shortcuts:</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-left">
            <span>Save file</span>
            <span className="text-zinc-500">Ctrl+S</span>
            <span>Format document</span>
            <span className="text-zinc-500">Shift+Alt+F</span>
            <span>Go to definition</span>
            <span className="text-zinc-500">F12</span>
            <span>Find references</span>
            <span className="text-zinc-500">Shift+F12</span>
            <span>Rename symbol</span>
            <span className="text-zinc-500">F2</span>
            <span>Quick fix</span>
            <span className="text-zinc-500">Ctrl+.</span>
          </div>
        </div>
      </div>
    </div>
  );
};
